import { postAuthSignOut, type User } from '@repo/api-portal-sdk/gen'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@repo/ui/dropdown-menu'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import {
	ChartNoAxesColumn,
	ChevronDown,
	ChevronRight,
	Globe2,
	LogOut,
	Menu,
	UserRound,
	X
} from 'lucide-react'
import { useState } from 'react'
import {
	type AppLanguageCode,
	changeAppLanguage,
	type TKey,
	translateApp,
	useTranslation
} from '@/lib/i18n'
import {
	type PortalLandingSessionState,
	portalLandingSessionQueryOptions
} from '@/lib/portal-session'

export function PortalLogo() {
	return (
		<Link className='portal-logo' to='/'>
			Sea<span>Ledger</span>
		</Link>
	)
}

export function MarketingHeader(props: {
	appLanguage: AppLanguageCode
	sessionState?: PortalLandingSessionState
}) {
	const [open, setOpen] = useState(false)
	const t = (key: TKey) => translateApp(props.appLanguage, key)
	const landingSessionQuery = useQuery({
		...portalLandingSessionQueryOptions(props.sessionState),
		enabled: typeof window !== 'undefined'
	})
	const sessionState = landingSessionQuery.data ??
		props.sessionState ?? { status: 'guest' as const }

	return (
		<header className='marketing-header sticky top-0 z-20'>
			<div className='portal-container marketing-header__inner relative flex h-[3.875rem] items-center justify-between gap-3 sm:h-[4.125rem] sm:gap-6'>
				<PortalLogo />
				<nav
					aria-label={t('common:header.primary_navigation')}
					className='marketing-nav ml-[clamp(5rem,16vw,11.875rem)] hidden items-center gap-[1.875rem] sm:flex'
				>
					<MarketingLinks onNavigate={() => setOpen(false)} t={t} />
				</nav>
				<div className='marketing-header__actions ml-auto flex items-center gap-2.5 sm:gap-8'>
					<LanguageSwitch appLanguage={props.appLanguage} />
					<MarketingAccess sessionState={sessionState} t={t} />
				</div>
				<button
					aria-expanded={open}
					aria-label={t('common:header.open_navigation')}
					className='icon-button marketing-header__menu inline-grid sm:hidden'
					onClick={() => setOpen((value) => !value)}
					type='button'
				>
					{open ? (
						<X aria-hidden='true' />
					) : (
						<Menu aria-hidden='true' />
					)}
				</button>
			</div>
			<div className='portal-container marketing-header__mobile-access grid grid-cols-2 gap-2 pb-2.5 sm:hidden'>
				<MarketingAccess sessionState={sessionState} t={t} />
			</div>
			{open ? (
				<nav
					aria-label={t('common:header.primary_navigation')}
					className='marketing-mobile-nav absolute inset-x-0 top-full z-10 grid w-full gap-2 border-(--portal-border) border-y bg-(--portal-surface) px-[1.125rem] py-3.5 pb-[1.125rem] shadow-[0_24px_48px_oklch(4%_0.02_255_/_0.62)] sm:hidden'
				>
					<MarketingLinks onNavigate={() => setOpen(false)} t={t} />
				</nav>
			) : null}
		</header>
	)
}

function MarketingLinks(props: {
	onNavigate: () => void
	t: (key: TKey) => string
}) {
	return (
		<>
			<Link hash='docs' onClick={props.onNavigate} to='/'>
				<span>{props.t('common:header.documentation')}</span>
				<ChevronRight aria-hidden='true' />
			</Link>
			<Link hash='prezzi' onClick={props.onNavigate} to='/'>
				<span>{props.t('common:header.pricing')}</span>
				<ChevronRight aria-hidden='true' />
			</Link>
		</>
	)
}

function MarketingAccess(props: {
	sessionState: PortalLandingSessionState
	t: (key: TKey) => string
}) {
	if (props.sessionState.status === 'authenticated') {
		return (
			<AccountMenu
				csrfToken={props.sessionState.csrfToken}
				triggerVariant='compact'
				user={props.sessionState.user}
			/>
		)
	}

	if (props.sessionState.status === 'unknown') {
		return (
			<Link className='header-sign-in' to='/dashboard'>
				{props.t('common:header.account')}
			</Link>
		)
	}

	return (
		<>
			<Link className='header-sign-in' to='/login'>
				{props.t('common:header.sign_in')}
			</Link>
			<Link
				className='portal-button portal-button--primary portal-button--small'
				to='/register'
			>
				{props.t('common:header.start_free')}
			</Link>
		</>
	)
}

function LanguageSwitch(props: { appLanguage: AppLanguageCode }) {
	const nextLanguage = props.appLanguage === 'en' ? 'it' : 'en'
	const label = translateApp(
		props.appLanguage,
		'common:header.switch_language'
	)

	async function switchLanguage() {
		await changeAppLanguage(nextLanguage)
		window.location.reload()
	}

	return (
		<button
			aria-label={label}
			className='header-language'
			onClick={switchLanguage}
			title={label}
			type='button'
		>
			<Globe2 aria-hidden='true' />
			{props.appLanguage.toUpperCase()}
		</button>
	)
}

export function AccountHeader(props: { user?: User }) {
	return (
		<header className='account-header'>
			<div className='portal-container account-header__inner flex h-[4.125rem] items-center justify-between gap-6'>
				<PortalLogo />
				<div className='flex items-center gap-3.5'>
					<AccountMenu user={props.user} />
				</div>
			</div>
		</header>
	)
}

function AccountMenu(props: {
	csrfToken?: string
	triggerVariant?: 'default' | 'compact'
	user?: User
}) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [open, setOpen] = useState(false)
	const [signOutError, setSignOutError] = useState<string | null>(null)
	const email = props.user?.email ?? t('common:header.account')

	async function signOut() {
		setSignOutError(null)
		try {
			await postAuthSignOut({
				body: {},
				headers: props.csrfToken
					? { 'X-CSRF-Token': props.csrfToken }
					: undefined
			})
			queryClient.clear()
			setOpen(false)
			await navigate({ to: '/' })
		} catch {
			setSignOutError(t('common:header.sign_out_failed'))
		}
	}

	return (
		<div className='account-menu'>
			<DropdownMenu onOpenChange={setOpen} open={open}>
				<DropdownMenuTrigger
					aria-label={t('common:header.open_account_menu')}
					className='inline-flex items-center gap-2.5 rounded-[10px] border-0 bg-transparent px-2 py-1 text-(--portal-fg) hover:bg-(--portal-surface) max-sm:[&>.portal-meta]:hidden max-sm:[&>svg]:hidden'
				>
					<span className='account-avatar grid size-8 place-items-center rounded-full border border-(--portal-border-strong) bg-(--portal-surface-raised) text-(--portal-gold) [&_svg]:size-4'>
						<UserRound aria-hidden='true' />
					</span>
					{props.triggerVariant === 'compact' ? null : (
						<span className='portal-meta'>{email}</span>
					)}
					<ChevronDown
						aria-hidden='true'
						className='size-3.5 text-(--portal-muted)'
					/>
				</DropdownMenuTrigger>
				<DropdownMenuContent
					align='end'
					className='account-menu__content'
					sideOffset={8}
				>
					<DropdownMenuGroup>
						<DropdownMenuLabel className='account-menu__label'>
							{email}
						</DropdownMenuLabel>
						<DropdownMenuItem
							className='account-menu__item'
							onClick={async () => {
								await navigate({ to: '/dashboard' })
							}}
						>
							<ChartNoAxesColumn aria-hidden='true' />
							{t('common:header.dashboard')}
						</DropdownMenuItem>
						<DropdownMenuItem
							className='account-menu__item'
							onClick={async () => {
								await navigate({ to: '/account' })
							}}
						>
							<UserRound aria-hidden='true' />
							{t('common:header.account')}
						</DropdownMenuItem>
						<DropdownMenuSeparator />
						<DropdownMenuItem
							className='account-menu__item account-menu__item--danger'
							onClick={signOut}
							variant='destructive'
						>
							<LogOut aria-hidden='true' />
							{t('common:header.sign_out')}
						</DropdownMenuItem>
					</DropdownMenuGroup>
				</DropdownMenuContent>
			</DropdownMenu>
			{signOutError ? (
				<div className='account-menu__error' role='alert'>
					<p>{signOutError}</p>
					<button
						className='account-menu__retry'
						onClick={signOut}
						type='button'
					>
						{t('common:header.retry_sign_out')}
					</button>
				</div>
			) : null}
		</div>
	)
}

export function PortalFooter(props: { appLanguage: AppLanguageCode }) {
	const t = (key: TKey) => translateApp(props.appLanguage, key)

	return (
		<footer className='portal-footer border-(--portal-border) border-t px-0 py-14 pb-6 text-[13px]'>
			<div className='portal-container portal-footer__grid grid grid-cols-2 gap-[1.875rem] md:grid-cols-[2fr_1fr_1fr] md:gap-10 lg:grid-cols-[2fr_repeat(3,1fr)]'>
				<div className='col-span-full lg:col-span-1'>
					<PortalLogo />
					<p>{t('common:footer.description')}</p>
					<span className='portal-meta'>support@sealedger.cards</span>
				</div>
				<div className='grid content-start gap-2.5'>
					<h2>{t('common:footer.product')}</h2>
					<a href='/#docs'>{t('common:header.documentation')}</a>
					<a href='/#prezzi'>{t('common:header.pricing')}</a>
					<Link to='/dashboard'>{t('common:header.dashboard')}</Link>
				</div>
				<div className='grid content-start gap-2.5'>
					<h2>{t('common:footer.resources')}</h2>
					<Link to='/changelog'>{t('common:footer.changelog')}</Link>
					<Link to='/status'>{t('common:footer.status')}</Link>
					<a href='/openapi.json'>{t('common:footer.openapi')}</a>
				</div>
				<div className='grid content-start gap-2.5'>
					<h2>{t('common:footer.legal')}</h2>
					<Link to='/terms'>{t('common:footer.terms')}</Link>
					<Link to='/privacy'>{t('common:footer.privacy')}</Link>
				</div>
			</div>
			<div className='portal-container portal-footer__bottom mt-11 grid gap-4 border-(--portal-border) border-t pt-5 font-mono text-[11px] sm:flex sm:justify-between'>
				<span>{t('common:footer.copyright')}</span>
				<span>{t('common:footer.made_for')}</span>
			</div>
		</footer>
	)
}
